import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Modal } from 'react-bootstrap';
import '../AppsMenu/Personell/employee-form.css';

/**
 * A small form in a dialog, awaited like a confirm:
 *
 *   const values = await formDialog({
 *     title: 'Markera AGI som skickad',
 *     description: 'Bekräfta att du har skickat filen.',
 *     fields: [
 *       { name: 'submissionDate', label: 'Datum skickad', type: 'date', required: true, defaultValue: today },
 *       { name: 'notes', label: 'Anteckning', type: 'textarea' },
 *     ],
 *     confirmLabel: 'Markera som skickad',
 *   });
 *   if (!values) return; // cancelled
 *
 * Replaces the SweetAlert dialogs with raw <input class="swal2-input"> HTML
 * the payroll steps used: those rendered in SweetAlert's own style with
 * placeholder-only labels. This one is the app's modal chrome and inputs.
 *
 * Rendered into its own root outside the React tree (React 17, so
 * ReactDOM.render), which is why it needs no provider and can be awaited
 * from an event handler. It does not read Redux or router context.
 */

function FormDialog({ title, description, fields, confirmLabel, cancelLabel, tone, resolve }) {
    const [show, setShow] = useState(true);
    const [values, setValues] = useState(() =>
        Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? ''])));
    const [touched, setTouched] = useState(false);

    const missing = fields.filter((f) => f.required && !String(values[f.name] ?? '').trim());
    const close = (result) => {
        setShow(false);
        resolve(result);
    };
    const submit = (e) => {
        e?.preventDefault();
        setTouched(true);
        if (missing.length) return;
        close(values);
    };

    return (
        <Modal className="emp-modal" show={show} onHide={() => close(null)} centered onExited={() => resolve.unmount()}>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <form onSubmit={submit} noValidate>
                <Modal.Body>
                    {description && <p className="mb-3" style={{ fontSize: '0.875rem', color: '#4b5563' }}>{description}</p>}
                    <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                        {fields.map((f, i) => {
                            const invalid = touched && f.required && !String(values[f.name] ?? '').trim();
                            const id = `form-dialog-${f.name}`;
                            const common = {
                                id,
                                name: f.name,
                                className: `modern-input${invalid ? ' error' : ''}`,
                                value: values[f.name] ?? '',
                                onChange: (e) => setValues((v) => ({ ...v, [f.name]: e.target.value })),
                                placeholder: f.placeholder,
                                autoFocus: i === 0,
                            };
                            return (
                                <div className="input-group" key={f.name}>
                                    <label className="input-label" htmlFor={id}>
                                        {f.label}
                                        {f.required && <span className="required-indicator">*</span>}
                                    </label>
                                    <div className="input-container">
                                        {f.type === 'textarea'
                                            ? <textarea {...common} rows={f.rows || 2} />
                                            : <input {...common} type={f.type || 'text'} />}
                                    </div>
                                    {f.hint && <p className="input-description">{f.hint}</p>}
                                    {invalid && <div className="error-message">{f.label} krävs</div>}
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="ll-btn ll-btn--secondary" onClick={() => close(null)}>
                        {cancelLabel}
                    </button>
                    <button type="submit" className={`ll-btn ll-btn--${tone}`}>
                        {confirmLabel}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

export default function formDialog({
    title,
    description,
    fields = [],
    confirmLabel = 'OK',
    cancelLabel = 'Avbryt',
    tone = 'primary',
}) {
    return new Promise((resolvePromise) => {
        const host = document.createElement('div');
        document.body.appendChild(host);
        let settled = false;
        const resolve = (result) => {
            if (settled) return;
            settled = true;
            resolvePromise(result);
        };
        resolve.unmount = () => {
            ReactDOM.unmountComponentAtNode(host);
            host.remove();
        };
        ReactDOM.render(
            <FormDialog
                title={title}
                description={description}
                fields={fields}
                confirmLabel={confirmLabel}
                cancelLabel={cancelLabel}
                tone={tone}
                resolve={resolve}
            />,
            host,
        );
    });
}
